// src/ViewReviewsPage.tsx
import { Helmet } from "react-helmet";
import React, { useState, useMemo, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Rating,
  Stack,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { fetchReviews, Review } from "../helpers/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { contentReveal } from "../motion";

interface ViewFormInputs {
  address: string;
}

const ViewReviewsPage: React.FC = () => {
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ViewFormInputs>({ defaultValues: { address: "" } });

  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  // Filter & sort state
  const [keyword, setKeyword] = useState("");
  const [minManagement, setMinManagement] = useState<string>("");
  const [minArea, setMinArea] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

  const addressValue = watch("address");
  const navigate = useNavigate();

  useEffect(() => {
    const addr = searchParams.get("address");
    if (addr != null && addr !== "") {
      setValue("address", addr, { shouldDirty: true });
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: ViewFormInputs) => {
    setLoading(true);
    setReviews(null);
    try {
      const result = await fetchReviews(data.address);
      setReviews(result);
    } catch (err: any) {
      console.error("Error fetching reviews:", err);
      setErrorMsg("Could not fetch reviews. Check the exact address.");
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Apply keyword + rating filters and sort by date
  const filteredAndSorted = useMemo<Review[]>(() => {
    if (!reviews) return [];
    let arr = [...reviews];

    if (keyword.trim()) {
      const lc = keyword.toLowerCase();
      arr = arr.filter((r) => r.review_text.toLowerCase().includes(lc));
    }
    if (minManagement) {
      const min = parseInt(minManagement, 10);
      arr = arr.filter((r) => r.management_rating >= min);
    }
    if (minArea) {
      const min = parseInt(minArea, 10);
      arr = arr.filter((r) => r.area_rating >= min);
    }
    arr.sort((a, b) => {
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return sortOrder === "newest" ? db - da : da - db;
    });
    return arr;
  }, [reviews, keyword, minManagement, minArea, sortOrder]);

  const handleCloseSnackbar = (_: any, reason?: string) => {
    if (reason === "clickaway") return;
    setErrorOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>View Apartment Reviews | Rate My Cribs</title>
        <meta
          name="description"
          content="Search and read honest reviews for apartments and rental properties. Find the best places to live and make informed decisions with real tenant feedback on Rate My Cribs."
        />
        <meta
          name="keywords"
          content="view reviews, apartment reviews, rental property, tenant feedback, rate my cribs, best apartments, honest reviews, landlord ratings"
        />
        <link rel="canonical" href="https://ratemycribs.com/reviews" />
        <meta
          property="og:title"
          content="View Apartment Reviews | Rate My Cribs"
        />
        <meta
          property="og:description"
          content="Search and read honest reviews for apartments and rental properties. Find the best places to live and make informed decisions with real tenant feedback on Rate My Cribs."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ratemycribs.com/reviews" />
        <meta
          property="og:image"
          content="https://ratemycribs.com/og-image.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="View Apartment Reviews | Rate My Cribs"
        />
        <meta
          name="twitter:description"
          content="Search and read honest reviews for apartments and rental properties. Find the best places to live and make informed decisions with real tenant feedback on Rate My Cribs."
        />
        <meta
          name="twitter:image"
          content="https://ratemycribs.com/og-image.png"
        />
      </Helmet>
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          py: 7,
          m: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 5 },
            maxWidth: 720,
            width: "97%",
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: (t) =>
              `0 24px 64px ${t.palette.mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(0,0,0,0.35)"}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            animation: "slideUp 0.55s ease-out both",
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
            "@media (prefers-reduced-motion: no-preference)": {
              transition: "box-shadow 0.35s ease, transform 0.35s ease",
              "&:hover": {
                boxShadow: (t) =>
                  `0 28px 72px ${t.palette.mode === "light" ? "rgba(15,23,42,0.1)" : "rgba(0,0,0,0.4)"}`,
                transform: "translateY(-2px)",
              },
            },
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 800, color: "primary.main", mb: 2 }}
          >
            View Property Reviews
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Enter an address to see reviews for that property. If you don't see
            any reviews, consider submitting the first one!
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ width: "100%", animation: "fadeIn 0.5s" }}
          >
            <Controller
              name="address"
              control={control}
              rules={{ required: "Address is required" }}
              render={({ field }) => (
                <AddressAutocomplete
                  value={addressValue}
                  onChange={field.onChange}
                  placeholder="Type address to look up reviews…"
                />
              )}
            />
            {errors.address && (
              <Typography color="error" variant="caption">
                {errors.address.message}
              </Typography>
            )}
            <Box mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !addressValue}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: 16 }}
              >
                {loading ? <CircularProgress size={20} /> : "Fetch Reviews"}
              </Button>
            </Box>
          </Box>

          {/* filters & sort */}
          {reviews && (
            <Box
              mt={4}
              display="flex"
              flexWrap="wrap"
              gap={2}
              width="100%"
              justifyContent="center"
            >
              <TextField
                label="Keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                size="small"
              />

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Management</InputLabel>
                <Select
                  label="Management"
                  value={minManagement}
                  onChange={(e: SelectChangeEvent) =>
                    setMinManagement(e.target.value)
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n}+
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Area</InputLabel>
                <Select
                  label="Area"
                  value={minArea}
                  onChange={(e: SelectChangeEvent) =>
                    setMinArea(e.target.value)
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n}+
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sort by Date</InputLabel>
                <Select
                  label="Sort by Date"
                  value={sortOrder}
                  onChange={(e: SelectChangeEvent) =>
                    setSortOrder(e.target.value as "newest" | "oldest")
                  }
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* reviews list */}
          {reviews !== null && (
            <Box mt={4} width="100%" aria-live="polite" aria-atomic="false">
              {filteredAndSorted.length === 0 ? (
                <Box textAlign="center">
                  <Typography>No reviews for this address yet.</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2, fontWeight: 600 }}
                    onClick={() => navigate("/submit")}
                  >
                    Submit the First Review!
                  </Button>
                </Box>
              ) : (
                <List sx={{ width: "100%" }}>
                  {filteredAndSorted.map((r, index) => (
                    <React.Fragment key={r.id}>
                      <ListItem disableGutters sx={{ py: 1.25 }}>
                        <Paper
                          component="article"
                          aria-label={`Review from ${new Date(
                            r.from_date
                          ).toLocaleDateString()}`}
                          elevation={0}
                          sx={{
                            width: "100%",
                            p: 2.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            animation: `${contentReveal} 0.5s ease backwards`,
                            animationDelay: `${Math.min(index, 14) * 0.055}s`,
                            transition:
                              "box-shadow 0.2s ease, transform 0.2s ease",
                            "&:hover": {
                              boxShadow: (t) =>
                                `0 12px 32px ${t.palette.mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(0,0,0,0.25)"}`,
                              transform: "translateY(-2px)",
                            },
                            "@media (prefers-reduced-motion: reduce)": {
                              animation: "none",
                              animationDelay: 0,
                              transition: "none",
                              "&:hover": { transform: "none" },
                            },
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary">
                            Submitted: {new Date(r.created_at).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Lived from{" "}
                            {new Date(r.from_date).toLocaleDateString()} to{" "}
                            {new Date(r.to_date).toLocaleDateString()}
                          </Typography>
                          <Stack spacing={1.25} mt={2}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontWeight={700}
                              >
                                Management
                              </Typography>
                              <Rating
                                value={r.management_rating}
                                readOnly
                                size="small"
                                getLabelText={(v) =>
                                  `Management rated ${v} of 5 stars`
                                }
                              />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontWeight={700}
                              >
                                Area
                              </Typography>
                              <Rating
                                value={r.area_rating}
                                readOnly
                                size="small"
                                getLabelText={(v) => `Area rated ${v} of 5 stars`}
                              />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontWeight={700}
                              >
                                Safety
                              </Typography>
                              <Rating
                                value={r.safety_rating}
                                readOnly
                                size="small"
                                getLabelText={(v) =>
                                  `Safety rated ${v} of 5 stars`
                                }
                              />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontWeight={700}
                              >
                                Maintenance
                              </Typography>
                              <Rating
                                value={r.maintenance_rating}
                                readOnly
                                size="small"
                                getLabelText={(v) =>
                                  `Maintenance rated ${v} of 5 stars`
                                }
                              />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontWeight={700}
                              >
                                Amenities
                              </Typography>
                              <Rating
                                value={r.amenities_rating}
                                readOnly
                                size="small"
                                getLabelText={(v) =>
                                  `Amenities rated ${v} of 5 stars`
                                }
                              />
                            </Box>
                          </Stack>
                          {r.review_text && (
                            <Typography variant="body1" mt={2} sx={{ lineHeight: 1.7 }}>
                              <Box component="span" sx={{ color: "text.secondary" }}>
                                “
                              </Box>
                              {r.review_text}
                              <Box component="span" sx={{ color: "text.secondary" }}>
                                ”
                              </Box>
                            </Typography>
                          )}
                        </Paper>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Paper>

        {/* error snackbar */}
        <Snackbar
          open={errorOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error">{errorMsg}</Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default ViewReviewsPage;
